import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import {
    Box, Button, Container, Paper, Stack, TextField, ToggleButton,
    ToggleButtonGroup, Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import generatePiecePaths from "../jigsaw/generatePiecePaths";

const TILE_OPTIONS = [
    { value: 6, label: "6×6" },
    { value: 9, label: "9×9" },
    { value: 12, label: "12×12" },
    { value: 5, label: "5×3" },
];

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

export default function SvgToJsonPage() {
    const navigate = useNavigate();
    const [svgFile, setSvgFile] = useState(null);
    const [tileSize, setTileSize] = useState(6);
    const [jsonOutput, setJsonOutput] = useState("");
    const [error, setError] = useState(null);
    const fileRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0] ?? null;
        setSvgFile(file);
        setError(null);
        setJsonOutput("");
    };

    const handleConvert = async () => {
        if (!svgFile) {
            setError("Select an SVG file first.");
            return;
        }
        setError(null);
        try {
            const svgText = await svgFile.text();
            const pieces = generatePiecePaths(svgText, tileSize);
            const result = JSON.stringify({ pieces }, null, 2);
            setJsonOutput(result);
        } catch (e) {
            setError(e.message || "Conversion failed");
            setJsonOutput("");
        }
    };

    const handleCopy = () => {
        if (jsonOutput) {
            navigator.clipboard.writeText(jsonOutput);
        }
    };

    const handleDownload = () => {
        if (!jsonOutput) return;
        const blob = new Blob([jsonOutput], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "piecePaths.json";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/system/anything")} sx={{ mb: 2 }}>
                Back
            </Button>
            <Typography variant="h5" sx={{ mb: 3 }}>Convert SVG to JSON</Typography>

            <Paper sx={{ p: 3 }}>
                <Stack spacing={3}>
                    {/* Tile size */}
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Tile Size</Typography>
                        <ToggleButtonGroup
                            exclusive
                            value={tileSize}
                            onChange={(_, v) => { if (v != null) setTileSize(v); }}
                            size="small"
                        >
                            {TILE_OPTIONS.map((opt) => (
                                <ToggleButton key={opt.value} value={opt.value} sx={{ px: 2 }}>
                                    {opt.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    {/* File picker */}
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>SVG File</Typography>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            size="small"
                        >
                            Choose SVG file
                            <VisuallyHiddenInput
                                ref={fileRef}
                                type="file"
                                accept=".svg,image/svg+xml"
                                onChange={handleFileChange}
                            />
                        </Button>
                        {svgFile && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {svgFile.name} ({(svgFile.size / 1024).toFixed(1)} KB)
                            </Typography>
                        )}
                    </Box>

                    {/* Convert button */}
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" onClick={handleConvert} disabled={!svgFile}>
                            Convert
                        </Button>
                        {jsonOutput && (
                            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
                                Download piecePaths.json
                            </Button>
                        )}
                    </Stack>

                    {error && <Typography color="error">{error}</Typography>}

                    {/* JSON output */}
                    {jsonOutput && (
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>JSON Output</Typography>
                                <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
                                    Copy
                                </Button>
                            </Box>
                            <TextField
                                multiline
                                fullWidth
                                value={jsonOutput}
                                minRows={10}
                                maxRows={30}
                                InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
                            />
                        </Box>
                    )}
                </Stack>
            </Paper>
        </Container>
    );
}
